import type { MediatorMergeBindingsContext } from '@comunica/bus-merge-bindings-context';
import type { MediatorQueryOperation } from '@comunica/bus-query-operation';
import type {
  IActionRdfJoin,
  IActorRdfJoinOutputInner,
  IActorRdfJoinArgs,
  IActorRdfJoinTestSideData,
} from '@comunica/bus-rdf-join';
import { ActorRdfJoin } from '@comunica/bus-rdf-join';
import type { MediatorRdfJoinEntriesSort } from '@comunica/bus-rdf-join-entries-sort';
import { KeysInitQuery, KeysQueryOperation } from '@comunica/context-entries';
import type { TestResult } from '@comunica/core';
import { passTestWithSideData, failTest } from '@comunica/core';
import type { IMediatorTypeJoinCoefficients } from '@comunica/mediatortype-join-coefficients';
import type {
  Bindings,
  BindingsStream,
  ComunicaDataFactory,
  IJoinEntryWithMetadata,
  IQueryOperationResultBindings,
} from '@comunica/types';
import { AlgebraFactory, Algebra, algebraUtils, inScopeVariables } from '@comunica/utils-algebra';
import { BindingsFactory } from '@comunica/utils-bindings-factory';
import { getSafeBindings, materializeOperation } from '@comunica/utils-query-operation';
import type * as RDF from '@rdfjs/types';
import { MultiTransformIterator, TransformIterator, UnionIterator } from 'asynciterator';

/**
 * A comunica Multi-way Bind RDF Join Actor.
 */
export class ActorRdfJoinMultiBind extends ActorRdfJoin<IActorRdfJoinMultiBindTestSideData> {
  public readonly bindOrder: BindOrder;
  public readonly selectivityModifier: number;
  public readonly minMaxCardinalityRatio: number;
  public readonly subQueryCost: number;
  public readonly mediatorJoinEntriesSort: MediatorRdfJoinEntriesSort;
  public readonly mediatorQueryOperation: MediatorQueryOperation;
  public readonly mediatorMergeBindingsContext: MediatorMergeBindingsContext;

  public constructor(args: IActorRdfJoinMultiBindArgs) {
    super(args, {
      logicalType: 'inner',
      physicalName: 'bind',
      canHandleUndefs: true,
      canHandleOperationRequired: true,
      isLeaf: false,
    });
    this.bindOrder = args.bindOrder;
    this.selectivityModifier = args.selectivityModifier;
    this.minMaxCardinalityRatio = args.minMaxCardinalityRatio;
    this.subQueryCost = args.subQueryCost ?? 100;
    this.mediatorJoinEntriesSort = args.mediatorJoinEntriesSort;
    this.mediatorQueryOperation = args.mediatorQueryOperation;
    this.mediatorMergeBindingsContext = args.mediatorMergeBindingsContext;
  }

  /**
   * Create a new bindings stream that takes every binding of the base stream
   * and binds it to the remaining patterns, evaluates those patterns, and emits all their bindings.
   *
   * @param bindOrder The order in which elements should be bound.
   * @param baseStream The base stream.
   * @param operations The operations to bind with each binding of the base stream.
   * @param operationBinder A callback to retrieve the bindings stream of bound operations.
   * @param optional If the original bindings should be emitted when the resulting bindings stream is empty.
   * @return {BindingsStream}
   */
  public static createBindStream(
    bindOrder: BindOrder,
    baseStream: BindingsStream,
    operations: Algebra.Operation[],
    operationBinder: (boundOperations: Algebra.Operation[], operationBindings: Bindings)
    => Promise<BindingsStream>,
    optional: boolean,
    algebraFactory: AlgebraFactory,
    bindingsFactory: BindingsFactory,
  ): BindingsStream {
    // Enable auto-start on sub-bindings during depth-first binding for best performance.
    const autoStartSubBindings = bindOrder === 'depth-first';

    // Create bindings function
    const binder = (bindings: Bindings): BindingsStream => {
      // We don't bind the filter because filters are always handled last,
      // and we need to avoid binding filters of sub-queries, which are to be handled first. (see spec test bind10)
      const subOperations = operations.map(operation => materializeOperation(
        operation,
        bindings,
        algebraFactory,
        bindingsFactory,
        { bindFilter: true },
      ));
      const bindingsMerger = (subBindings: Bindings): Bindings | undefined => subBindings.merge(bindings);
      return new TransformIterator(async() => (await operationBinder(subOperations, bindings))
        .transform({ map: bindingsMerger }), { maxBufferSize: 128, autoStart: autoStartSubBindings });
    };

    // Create an iterator that binds elements from the base stream in different orders
    switch (bindOrder) {
      case 'depth-first':
        return new MultiTransformIterator(baseStream, { autoStart: false, multiTransform: binder, optional });
      case 'breadth-first':
        return new UnionIterator(baseStream.transform({
          map: binder,
          optional,
          autoStart: false,
        }), { autoStart: false });
      default:
        // eslint-disable-next-line ts/restrict-template-expressions
        throw new Error(`Received request for unknown bind order: ${bindOrder}`);
    }
  }

  public async getOutput(
    action: IActionRdfJoin,
    sideData: IActorRdfJoinMultiBindTestSideData,
  ): Promise<IActorRdfJoinOutputInner> {
    const dataFactory: ComunicaDataFactory = action.context.getSafe(KeysInitQuery.dataFactory);
    const algebraFactory = new AlgebraFactory(dataFactory);
    const bindingsFactory = await BindingsFactory.create(
      this.mediatorMergeBindingsContext,
      action.context,
      dataFactory,
    );

    const entries = sideData.entriesSorted;
    this.logDebug(
      action.context,
      'First entry for Bind Join: ',
      () => ({
        entry: { ...entries[0].operation, metadata: undefined },
        cardinality: entries[0].metadata.cardinality,
        order: entries[0].metadata.order,
        availableOrders: entries[0].metadata.availableOrders,
      }),
    );

    // Destroy the non-smallest streams
    for (const [ i, element ] of entries.entries()) {
      if (i !== 0) {
        element.output.bindingsStream.destroy();
      }
    }

    // Take the stream with the lowest cardinality
    const smallestStream: IQueryOperationResultBindings = entries[0].output;
    const remainingEntries = [ ...entries ];
    remainingEntries.splice(0, 1);

    // Bind the remaining patterns for each binding in the stream
    const subContext = action.context
      .set(KeysQueryOperation.joinLeftMetadata, entries[0].metadata)
      .set(KeysQueryOperation.joinRightMetadatas, remainingEntries.map(entry => entry.metadata));
    const bindingsStream: BindingsStream = ActorRdfJoinMultiBind.createBindStream(
      this.bindOrder,
      smallestStream.bindingsStream,
      remainingEntries.map(entry => entry.operation),
      async(operations: Algebra.Operation[], operationBindings: Bindings) => {
        // Send the materialized patterns to the mediator for recursive join evaluation.
        const operation = operations.length === 1 ?
          operations[0] :
          // Flattening should only take place if none of the input operations have associated metadata
          algebraFactory.createJoin(operations, operations.every(op => !op.metadata));
        const output = getSafeBindings(await this.mediatorQueryOperation.mediate(
          { operation, context: subContext?.set(KeysQueryOperation.joinBindings, operationBindings) },
        ));
        return output.bindingsStream;
      },
      false,
      algebraFactory,
      bindingsFactory,
    );

    return {
      result: {
        type: 'bindings',
        bindingsStream,
        metadata: () => this.constructResultMetadata(entries, entries.map(entry => entry.metadata), action.context),
      },
      physicalPlanMetadata: {
        bindIndex: sideData.entriesUnsorted.indexOf(entries[0]),
        bindOperation: entries[0].operation,
        bindOperationCardinality: entries[0].metadata.cardinality,
        bindOrder: this.bindOrder,
      },
    };
  }

  public static canBindWithOperation(operation: Algebra.Operation, boundVariables?: RDF.Variable[]): boolean {
    let valid = true;
    const boundVarNames = boundVariables ? boundVariables.map(v => v.value) : [];

    const skipHandler = {
      preVisitor: () => {
        valid = false;
        return { shortcut: true };
      },
    };

    algebraUtils.visitOperation(operation, {
      [Algebra.Types.EXTEND]: skipHandler,
      [Algebra.Types.GROUP]: skipHandler,
      [Algebra.Types.LEFT_JOIN]: {
        preVisitor: (op: Algebra.LeftJoin) => {
          // Default: valid
          if (!boundVariables) {
            return { shortcut: false };
          }

          const leftOp = op.input[0];
          const rightOp = op.input[1];

          const leftVars = new Set(inScopeVariables(leftOp).map(v => v.value));
          const rightVars = inScopeVariables(rightOp).map(v => v.value);

          const conflict = rightVars.some(v => !leftVars.has(v) && boundVarNames.includes(v));
          if (conflict) {
            valid = false;
            return { shortcut: true };
          }
          return { shortcut: false };
        },
      },
      [Algebra.Types.MINUS]: {
        preVisitor: (op: Algebra.Minus) => {
          // Default: valid
          if (!boundVariables) {
            return { shortcut: false };
          }

          const rightOp = op.input[1];
          const rightVars = inScopeVariables(rightOp).map(v => v.value);

          const conflict = rightVars.some(v => boundVarNames.includes(v));
          if (conflict) {
            valid = false;
            return { shortcut: true };
          }
          return { shortcut: false };
        },
      },
      [Algebra.Types.FILTER]: {
        preVisitor: (op: Algebra.Filter) => {
          // Conflict when FILTER is not a direct child of LEFT_JOIN
          if (!op.metadata?.isHoistedLeftJoinFilter) {
            valid = false;
            return { shortcut: true };
          }
          return { shortcut: false };
        },
      },
      [Algebra.Types.EXPRESSION]: {
        preVisitor: (op: Algebra.Expression) => {
          // Don't descend into the sub-pattern of a FILTER (NOT) EXISTS:
          // it is evaluated as its own independent query
          if (op.subType === Algebra.ExpressionTypes.EXISTENCE) {
            return { continue: false };
          }
          return { shortcut: false };
        },
      },
    });

    return valid;
  }

  public async getJoinCoefficients(
    action: IActionRdfJoin,
    sideData: IActorRdfJoinTestSideData,
  ): Promise<TestResult<IMediatorTypeJoinCoefficients, IActorRdfJoinMultiBindTestSideData>> {
    let { metadatas } = sideData;

    // Order the entries so we can pick the first one (usually the one with the lowest cardinality)
    const entriesUnsorted = action.entries
      .map((entry, i) => ({ ...entry, metadata: metadatas[i] }));
    const entriesTest = await ActorRdfJoin
      .sortJoinEntries(this.mediatorJoinEntriesSort, entriesUnsorted, action.context);
    if (entriesTest.isFailed()) {
      return entriesTest;
    }
    const entriesSorted = entriesTest.get();

    // Find the first entry that does not require operation pushdown.
    const bindableIndex = entriesSorted
      .findIndex(entry => !ActorRdfJoin.isOperationRequired(entry, entry.metadata));
    if (bindableIndex < 0) {
      return failTest(`Actor ${this.name} requires at least one entry of which the operation does not need to be pushed down`);
    }
    // This case could occur for variable SERVICE targets, which need to be bound later.
    if (bindableIndex > 0) {
      entriesSorted.unshift(...entriesSorted.splice(bindableIndex, 1));
    }

    metadatas = entriesSorted.map(entry => entry.metadata);

    const requestInitialTimes = ActorRdfJoin.getRequestInitialTimes(metadatas);
    const requestItemTimes = ActorRdfJoin.getRequestItemTimes(metadatas);

    // Determine first stream and remaining ones
    const remainingEntries = [ ...entriesSorted ];
    const remainingRequestInitialTimes = [ ...requestInitialTimes ];
    const remainingRequestItemTimes = [ ...requestItemTimes ];
    remainingEntries.splice(0, 1);
    remainingRequestInitialTimes.splice(0, 1);
    remainingRequestItemTimes.splice(0, 1);

    // Reject binding on some operation types
    if (remainingEntries
      .some(entry => !ActorRdfJoinMultiBind.canBindWithOperation(entry.operation))) {
      return failTest(`Actor ${this.name} can not bind on Extend and Group operations`);
    }

    // Reject binding on modified operations, since using the output directly would be significantly more efficient.
    if (remainingEntries.some(entry => entry.operationModified)) {
      return failTest(`Actor ${this.name} can not be used over remaining entries with modified operations`);
    }

    // Only run this actor if the smallest stream is significantly smaller than the largest stream.
    // We must use Math.max, because the last metadata is not necessarily the biggest, but it's the least preferred.
    // If join entries are produced locally, we increase the possibility of doing this bind join, as it's cheap.
    const isRemoteAccess = requestItemTimes.some(time => time > 0);
    if (metadatas[0].cardinality.value * this.minMaxCardinalityRatio / (isRemoteAccess ? 1 : 3) >
      Math.max(...metadatas.map(metadata => metadata.cardinality.value))) {
      return failTest(`Actor ${this.name} can only run if the smallest stream is much smaller than largest stream`);
    }

    // Determine selectivities of smallest entry with all other entries
    const selectivities = await Promise.all(remainingEntries
      .map(async entry => (await this.mediatorJoinSelectivity.mediate({
        entries: [ entriesSorted[0], entry ],
        context: action.context,
      })).selectivity * this.selectivityModifier));

    // Bindings each remaining entry adds per binding of the first: their pairwise join capped by the
    // variables they share, spread over the first entry's cardinality, so at most one. Entries sharing no
    // variable have no such cap and keep using the join selectivity.
    const cardinalityFirst = metadatas[0].cardinality.value;
    let entriesWithoutSharedVariable = 0;
    const cardinalityRemaining = remainingEntries
      .map((entry, i) => {
        const joined = ActorRdfJoin.getSharedVariableJoinCardinality([ metadatas[0], entry.metadata ]);
        if (joined === undefined) {
          entriesWithoutSharedVariable++;
          return entry.metadata.cardinality.value * selectivities[i];
        }
        return joined / cardinalityFirst;
      })
      .reduce((sum, element) => sum + element, 0);
    const receiveInitialCostRemaining = remainingRequestInitialTimes
      .reduce((sum, element) => sum + element, 0);
    const receiveItemCostRemaining = remainingRequestItemTimes
      .reduce((sum, element) => sum + element, 0);

    // Each binding re-plans and re-runs every remaining operation.
    // This does not apply to remote sources, where this cost is already incorporated into requestTime.
    const subPlanCost = isRemoteAccess ?
      0 :
      this.subQueryCost * remainingEntries.length * (1 + entriesWithoutSharedVariable);

    return passTestWithSideData({
      iterations: cardinalityFirst * (cardinalityRemaining + subPlanCost),
      persistedItems: 0,
      blockingItems: 0,
      requestTime: requestInitialTimes[0] +
        cardinalityFirst * (
          requestItemTimes[0] +
          receiveInitialCostRemaining +
          cardinalityRemaining * receiveItemCostRemaining
        ),
    }, { ...sideData, entriesUnsorted, entriesSorted });
  }
}

export interface IActorRdfJoinMultiBindArgs extends IActorRdfJoinArgs<IActorRdfJoinMultiBindTestSideData> {
  /**
   * The order in which elements should be bound
   * @default {depth-first}
   */
  bindOrder: BindOrder;
  /**
   * Multiplier for selectivity values
   * @range {double}
   * @default {0.0001}
   */
  selectivityModifier: number;
  /**
   * The number of times the smallest cardinality should fit in the maximum cardinality.
   * @range {double}
   * @default {60}
   */
  minMaxCardinalityRatio: number;
  // TODO: in next major, make mandatory.
  /**
   * The cost of planning and evaluating one bound operation, expressed in produced rows.
   * Not applied to remote sources.
   * @range {double}
   * @default {100}
   */
  subQueryCost?: number;
  /**
   * The join entries sort mediator
   */
  mediatorJoinEntriesSort: MediatorRdfJoinEntriesSort;
  /**
   * The query operation mediator
   */
  mediatorQueryOperation: MediatorQueryOperation;
  /**
   * A mediator for creating binding context merge handlers
   */
  mediatorMergeBindingsContext: MediatorMergeBindingsContext;
}

export type BindOrder = 'depth-first' | 'breadth-first';

export interface IActorRdfJoinMultiBindTestSideData extends IActorRdfJoinTestSideData {
  entriesUnsorted: IJoinEntryWithMetadata[];
  entriesSorted: IJoinEntryWithMetadata[];
}
